import { TrendingUp, Calendar, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const CaseStudy = () => {
  const metrics = [
    {
      icon: Calendar,
      label: "Weekly Inspections",
      before: "10",
      after: "25",
      change: "+150%"
    },
    {
      icon: Clock,
      label: "Avg Response Time",
      before: "3 hours",
      after: "8 seconds",
      change: "-99%"
    },
    {
      icon: Users,
      label: "Lead-to-Booking Rate",
      before: "18%",
      after: "42%",
      change: "+133%"
    }
  ];

  return (
    <section id="case-study-section" className="py-20 md:py-28 bg-white" data-testid="case-study-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Real Results
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            From 10 to 25 Inspections Per Week
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
            How one roofing company increased booked inspections by 30% within 30 days.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="rounded overflow-hidden shadow-lg">
              <img 
                src="https://images.pexels.com/photos/6837652/pexels-photo-6837652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Roofing team success"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
            </div>
            {/* Quote Overlay */}
            <Card className="absolute -bottom-6 -right-6 max-w-sm bg-white border-slate-200 rounded-sm shadow-xl hidden md:block">
              <CardContent className="p-6">
                <p className="text-slate-700 italic mb-4">
                  "We were skeptical about AI, but the numbers don't lie. Our team now focuses on selling instead of chasing leads."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    <span className="font-bold text-slate-600">M</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Mike R.</p>
                    <p className="text-sm text-slate-500">Owner, Apex Roofing</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Side */}
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-sm border border-slate-200 p-6 mb-8">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                The Challenge
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Apex Roofing (22 employees, Phoenix metro) was generating 80+ leads per month but only booking 10 inspections per week. Their office staff couldn't keep up with form submissions and callbacks, and after-hours leads were going cold overnight.
              </p>
            </div>

            {/* Metrics Cards */}
            <div className="space-y-4">
              {metrics.map((metric, index) => (
                <Card key={index} className="bg-white border-slate-200 rounded-sm" data-testid={`metric-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center">
                          <metric.icon className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">{metric.label}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-slate-400 line-through">{metric.before}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-heading text-2xl font-bold text-slate-900">{metric.after}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-100 text-green-700 font-bold text-sm px-3 py-1 rounded-sm">
                        {metric.change}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Result Summary */}
            <div className="bg-green-50 border border-green-200 rounded-sm p-6 mt-6">
              <p className="text-green-800 font-medium">
                <span className="font-bold">Bottom line:</span> Within 30 days of implementing our system, Apex Roofing increased booked inspections by 30% while their office staff had more time for customer service.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Quote */}
        <Card className="mt-8 bg-white border-slate-200 rounded-sm shadow-lg md:hidden">
          <CardContent className="p-6">
            <p className="text-slate-700 italic mb-4">
              "We were skeptical about AI, but the numbers don't lie. Our team now focuses on selling instead of chasing leads."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="font-bold text-slate-600">M</span>
              </div>
              <div>
                <p className="font-semibold text-slate-900">Mike R.</p>
                <p className="text-sm text-slate-500">Owner, Apex Roofing</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default CaseStudy;
